import React, { useEffect, useState } from "react";
import {
  Megaphone,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  Gift,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Plus,
  Settings2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Socio } from "../types/Socio.tsx";
import socioService from "../services/socioService.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdminRole } from "../hooks/useAdminRole.ts";
import {
  listarCampanias,
  listarCampaniasPorSocio,
  aceptarCampania,
  rechazarCampania,
  Campania,
  SocioCampania,
} from "../services/campaniaService.ts";

const estadoAsociacion = (estado: string) => {
  switch (estado) {
    case "aceptada":
      return { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Aceptada" };
    case "rechazada":
      return { color: "bg-rose-100 text-rose-700", icon: XCircle, label: "Rechazada" };
    case "pendiente":
      return { color: "bg-amber-100 text-amber-700", icon: Clock, label: "Pendiente" };
    default:
      return { color: "bg-slate-100 text-slate-600", icon: Clock, label: estado };
  }
};

export default function CampaniasPanel() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();
  const [socioData, setSocioData] = useState<Socio | null>(null);
  const [campanias, setCampanias] = useState<Campania[]>([]);
  const [misAsociaciones, setMisAsociaciones] = useState<SocioCampania[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accionando, setAccionando] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ tipo: "ok" | "error"; texto: string } | null>(null);

  useEffect(() => {
    const loadSocio = async () => {
      const stored = localStorage.getItem("socioSeleccionado");
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as Socio;
          setSocioData(parsed);
          if (parsed.socio_id) {
            const full = await socioService.getSocioById(parsed.socio_id, getAccessTokenSilently);
            setSocioData(full);
          }
        } catch (e) {
          console.error("Error al leer socio:", e);
        }
      }
    };
    loadSocio();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const activas = await listarCampanias("activa", token);
        setCampanias(activas);

        if (socioData?.socio_id) {
          const asociaciones = await listarCampaniasPorSocio(Number(socioData.socio_id), token);
          setMisAsociaciones(asociaciones);
        }
      } catch (e) {
        console.error("Error al cargar campañas:", e);
        setError("No se pudieron cargar las campañas. Intentá nuevamente.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [socioData, getAccessTokenSilently]);

  const getMiAsociacion = (campaniaId: number): SocioCampania | undefined =>
    misAsociaciones.find((a) => a.campania_id === campaniaId);

  const handleAceptar = async (socioCampaniaId: number) => {
    setAccionando(socioCampaniaId);
    setMsg(null);
    try {
      const token = await getAccessTokenSilently();
      await aceptarCampania(socioCampaniaId, token);
      setMsg({ tipo: "ok", texto: "Campaña aceptada correctamente" });
      const asociaciones = await listarCampaniasPorSocio(Number(socioData?.socio_id), token);
      setMisAsociaciones(asociaciones);
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al aceptar la campaña" });
    } finally {
      setAccionando(null);
    }
  };

  const handleRechazar = async (socioCampaniaId: number) => {
    setAccionando(socioCampaniaId);
    setMsg(null);
    try {
      const token = await getAccessTokenSilently();
      await rechazarCampania(socioCampaniaId, token);
      setMsg({ tipo: "ok", texto: "Campaña rechazada" });
      const asociaciones = await listarCampaniasPorSocio(Number(socioData?.socio_id), token);
      setMisAsociaciones(asociaciones);
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al rechazar la campaña" });
    } finally {
      setAccionando(null);
    }
  };

  const campaniasDisponibles = campanias.filter((c) => !getMiAsociacion(c.campania_id));
  const misCampanias = misAsociaciones.filter((a) => a.campania);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate("/inicio")}
          className="mb-6 flex items-center gap-1 text-sm font-medium text-slate-600 transition hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Volver al inicio
        </button>

        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Campañas</h1>
            <p className="mt-1 text-sm text-slate-500">Descubrí las campañas disponibles para vos</p>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/campanias-admin")}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                <Settings2 className="h-4 w-4" /> Gestionar campañas
              </button>
              <button
                onClick={() => navigate("/campanias-nueva")}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> Nueva campaña
              </button>
            </div>
          )}
        </div>

        {msg && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
              msg.tipo === "ok"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-rose-50 text-rose-700 border border-rose-200"
            }`}
          >
            {msg.tipo === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {msg.texto}
          </div>
        )}

        {error && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Mis Campañas */}
            {misCampanias.length > 0 && (
              <div className="mb-10">
                <h2 className="mb-4 text-lg font-semibold text-slate-900">Mis Campañas</h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {misCampanias.map((asociacion) => {
                    const campania = asociacion.campania!;
                    const estadoInfo = estadoAsociacion(asociacion.estado);
                    const EstadoIcon = estadoInfo.icon;
                    return (
                      <div
                        key={asociacion.socio_campania_id}
                        className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                      >
                        {campania.imagen_url ? (
                          <div className="h-36 overflow-hidden">
                            <img src={campania.imagen_url} alt={campania.nombre} className="h-full w-full object-cover" />
                          </div>
                        ) : (
                          <div className="flex h-36 items-center justify-center bg-gradient-to-r from-blue-500 to-indigo-600">
                            <Megaphone className="h-12 w-12 text-white/80" />
                          </div>
                        )}
                        <div className="p-5">
                          <div className="mb-2 flex items-start justify-between gap-2">
                            <h3 className="text-lg font-semibold text-slate-900">{campania.nombre}</h3>
                            <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${estadoInfo.color}`}>
                              <EstadoIcon className="h-3 w-3" />
                              {estadoInfo.label}
                            </span>
                          </div>
                          {campania.descripcion && (
                            <p className="mb-3 text-sm text-slate-600 line-clamp-2">{campania.descripcion}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="h-3.5 w-3.5" />
                              {new Date(campania.fecha_inicio).toLocaleDateString("es-AR")} - {new Date(campania.fecha_fin).toLocaleDateString("es-AR")}
                            </span>
                            {campania.descuento && (
                              <span className="flex items-center gap-1 font-semibold text-indigo-600">
                                <Gift className="h-3.5 w-3.5" />
                                {campania.descuento}
                              </span>
                            )}
                          </div>
                          {asociacion.estado === "pendiente" && (
                            <div className="mt-4 flex gap-2">
                              <button
                                disabled={accionando === asociacion.socio_campania_id}
                                onClick={() => handleAceptar(asociacion.socio_campania_id)}
                                className="flex flex-1 items-center justify-center gap-1 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                              >
                                {accionando === asociacion.socio_campania_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="h-4 w-4" />
                                )}
                                Aceptar
                              </button>
                              <button
                                disabled={accionando === asociacion.socio_campania_id}
                                onClick={() => handleRechazar(asociacion.socio_campania_id)}
                                className="flex flex-1 items-center justify-center gap-1 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                              >
                                <XCircle className="h-4 w-4" />
                                Rechazar
                              </button>
                            </div>
                          )}
                          <button
                            onClick={() => navigate(`/campanias/${campania.campania_id}`)}
                            className="mt-3 flex w-full items-center justify-center gap-1 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                          >
                            Ver detalle <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Campañas Disponibles */}
            <div>
              <h2 className="mb-4 text-lg font-semibold text-slate-900">Campañas Disponibles</h2>
              {campaniasDisponibles.length === 0 ? (
                <div className="rounded-2xl border border-slate-100 bg-white/70 p-12 text-center shadow-sm">
                  <Megaphone className="mx-auto h-12 w-12 text-slate-300" />
                  <p className="mt-4 text-sm text-slate-500">No hay campañas disponibles en este momento</p>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {campaniasDisponibles.map((campania) => (
                    <div
                      key={campania.campania_id}
                      className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                    >
                      {campania.imagen_url ? (
                        <div className="h-36 overflow-hidden">
                          <img src={campania.imagen_url} alt={campania.nombre} className="h-full w-full object-cover" />
                        </div>
                      ) : (
                        <div className="flex h-36 items-center justify-center bg-gradient-to-r from-indigo-500 to-purple-600">
                          <Megaphone className="h-12 w-12 text-white/80" />
                        </div>
                      )}
                      <div className="p-5">
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <h3 className="text-lg font-semibold text-slate-900">{campania.nombre}</h3>
                          <span className="inline-flex shrink-0 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                            {campania.estado}
                          </span>
                        </div>
                        {campania.descripcion && (
                          <p className="mb-3 text-sm text-slate-600 line-clamp-2">{campania.descripcion}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <CalendarDays className="h-3.5 w-3.5" />
                            {new Date(campania.fecha_inicio).toLocaleDateString("es-AR")} - {new Date(campania.fecha_fin).toLocaleDateString("es-AR")}
                          </span>
                          {campania.descuento && (
                            <span className="flex items-center gap-1 font-semibold text-indigo-600">
                              <Gift className="h-3.5 w-3.5" />
                              {campania.descuento}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => navigate(`/campanias/${campania.campania_id}`)}
                          className="mt-4 flex w-full items-center justify-center gap-1 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
                        >
                          Ver campaña <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}